import api from 'api'
import { DetailsValues, detailsValuesDefault } from 'contexts/CampaignFormContext'
import { CardStatus } from 'components/Layout/Card/types'
import {
  Campaign,
  CampaignBudgetCurrency,
  CampaignTarget,
  defaultCommercialAttribute,
  MediaFormat,
} from 'types/campaign'
import EnumUtils from 'utils/enum'
import { DetailsFormErrors, detailsFormErrorsEmpty } from '../types'
import VariableUtils from 'utils/variable'
import ErrorUtils from 'utils/error'
import { BaseRole, Priority, Role } from 'types/user'
import ArrayUtils from 'utils/array'
import { Dispatch, SetStateAction } from 'react'
import { detailsFormActions } from '../store/details-form-slice'
import { store } from 'store'
import { campaignFormActions } from '../../../store/campaign-form-slice'
import { UpdateMediaCriteriaSearchFR } from '../../../../../api/campaign/types'

export default class DetailsFormService {
  static hasErrors = (errors: DetailsFormErrors | undefined): boolean =>
    !VariableUtils.isDeepEqual(errors, detailsFormErrorsEmpty)

  static validate = (
    values: DetailsValues,
    setErrors: Dispatch<SetStateAction<DetailsFormErrors | undefined>>
  ): void => {
    setErrors(detailsFormErrorsEmpty)

    setErrors({
      budget: ErrorUtils.isSafeInteger(values.budget),
      audience: ErrorUtils.isSafeInteger(values.audience),
      emissions: ErrorUtils.isSafeInteger(values.emissions),
      hiddenImpressions: ErrorUtils.isSafeInteger(values.hiddenImpressions),
      hiddenAudience: ErrorUtils.isSafeInteger(values.hiddenAudience),
      guaranteedImpressions: ErrorUtils.isSafeInteger(values.guaranteedImpressions),
      frequency: ErrorUtils.isSafeInteger(values.frequency),
      intensityModifier: ErrorUtils.isIntensityModifierInRange(values.intensityModifier),
    })
  }

  static handleSubmit = (
    campaignId: string,
    data: DetailsValues,
    setTriggerFetchCampaign: Dispatch<SetStateAction<boolean>>,
    userRoles?: Role[]
  ): Promise<void> => {
    store.dispatch(campaignFormActions.setCardStatus({ details: CardStatus.SAVING }))

    const mutationResult: Promise<UpdateMediaCriteriaSearchFR> =
      DetailsFormService.getUpdateMediaCriteriaSearchMutation(data, campaignId, userRoles)

    return mutationResult
      .then(() => {
        setTriggerFetchCampaign(true)
        store.dispatch(campaignFormActions.setCardStatus({ details: CardStatus.SAVED }))
      })
      .catch(() => {
        setTriggerFetchCampaign(false)
        store.dispatch(campaignFormActions.setCardStatus({ details: CardStatus.ERROR }))
      })
      .finally(() => {
        store.dispatch(detailsFormActions.setDisableMediaSelection(!data.offer))
        store.dispatch(detailsFormActions.setDisableUsedMedia(false))
      })
  }

  static fetchValues = (campaignId: Campaign['id'], values: DetailsValues): void => {
    store.dispatch(campaignFormActions.setCardStatus({ details: CardStatus.LOADING }))

    const query = api.campaign.getCampaignForDetailsForm(campaignId)

    query
      .then(res => {
        const {
          budget,
          budgetCurrency,
          impressions,
          hiddenImpressions,
          hiddenImpressionsActive,
          mediaCriteriaSearch,
          priority,
          acceptanceStatus,
          target,
          offer,
          commercialAttribute,
          organizationAttribute,
          emissionType,
          audience,
          hiddenAudience,
          hiddenAudienceActive,
          mediaDuration,
          priorityRanges,
          creationContributionType,
          agencyDiscount,
          guaranteedImpressions,
          frequency,
          intensityModifier,
        } = res.data.node

        const newValues = mediaCriteriaSearch
          ? {
              mediumFormat:
                MediaFormat[
                  mediaCriteriaSearch.mediumFormat as unknown as keyof typeof MediaFormat
                ],
              pois: mediaCriteriaSearch.pois ?? [],
              target:
                CampaignTarget[
                  EnumUtils.getKeyByValue(CampaignTarget, target) as keyof typeof CampaignTarget
                ],
              budget: budget ?? 0,
              emissions: impressions ?? 0,
              hiddenImpressions: hiddenImpressions ?? 0,
              hiddenImpressionsActive,
              audience: audience ?? 0,
              hiddenAudience: hiddenAudience ?? 0,
              hiddenAudienceActive,
              emissionType,
              acceptanceStatus,
              budgetCurrency:
                CampaignBudgetCurrency[
                  EnumUtils.getKeyByValue(
                    CampaignBudgetCurrency,
                    budgetCurrency
                  ) as keyof typeof CampaignBudgetCurrency
                ],
              priority,
              mediaDuration: mediaDuration || detailsValuesDefault.mediaDuration,
              offer,
              commercialAttribute: commercialAttribute ?? defaultCommercialAttribute,
              organizationAttribute,
              priorityRanges,
              creationContributionType,
              agencyDiscount: (parseInt(agencyDiscount || '') * 100).toString(),
              guaranteedImpressions: guaranteedImpressions ?? 0,
              frequency: frequency ?? 0,
              intensityModifier: intensityModifier ?? null,
            }
          : {}
        const detailsValues = { ...values, ...newValues, fetched: true }

        store.dispatch(campaignFormActions.setCreationContributionType(creationContributionType))
        store.dispatch(campaignFormActions.updateDetailsValues(detailsValues))
        store.dispatch(detailsFormActions.setDisableMediaSelection(!detailsValues.offer))
        store.dispatch(campaignFormActions.setCardStatus({ details: CardStatus.NONE }))
      })
      .catch(() => {
        store.dispatch(campaignFormActions.setCardStatus({ details: CardStatus.ERROR }))
      })
  }

  static getMinMaxPriorities = (
    priorities: Campaign['priorityRanges']
  ): { min: Priority; max: Priority } => {
    if (!priorities || priorities.length === 0) {
      return {
        min: detailsValuesDefault.priority,
        max: detailsValuesDefault.priority,
      }
    }

    return {
      min: ArrayUtils.getMinMax([...priorities]).min,
      max: ArrayUtils.getMinMax([...priorities]).max,
    }
  }

  static getUpdateMediaCriteriaSearchMutation = (
    data: DetailsValues,
    campaignId: string,
    userRoles?: Role[]
  ): Promise<UpdateMediaCriteriaSearchFR> => {
    const {
      mediumFormat,
      hiddenImpressions,
      hiddenImpressionsActive,
      offer,
      emissionType,
      hiddenAudience,
      hiddenAudienceActive,
      guaranteedImpressions,
    } = data
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { acceptanceStatus, ...restDetailsValues } = data

    const variables = {
      ...data,
      campaignId,
      mediumFormat: EnumUtils.getKeyByValueT(MediaFormat, mediumFormat) as keyof MediaFormat,
    }

    if (userRoles && userRoles.some(role => role.baseRole === BaseRole.ADMIN)) {
      return api.campaign.updateMediaCriteriaSearchAdmin({
        ...variables,
        hiddenImpressions,
        hiddenImpressionsActive,
        offer,
        commercialAttribute: variables.commercialAttribute ?? defaultCommercialAttribute,
        emissionType,
        hiddenAudience,
        hiddenAudienceActive,
        guaranteedImpressions,
      })
    }

    if (userRoles && userRoles.some(role => role.baseRole === BaseRole.SUPPORT)) {
      return api.campaign.updateMediaCriteriaSearchSupport({
        ...variables,
        offer,
      })
    }

    if (userRoles && userRoles.some(role => role.baseRole === BaseRole.EMPLOYEE)) {
      return api.campaign.updateMediaCriteriaSearchEmployee({
        ...variables,
        commercialAttribute: variables.commercialAttribute ?? defaultCommercialAttribute,
      })
    }

    return api.campaign.updateMediaCriteriaSearch(variables)
  }

  static fetchSuggestedAudience = (campaignId: Campaign['id']): Promise<void> => {
    store.dispatch(campaignFormActions.setSuggestionsLoading(true))

    return api.campaign
      .getCampaignSuggestedAudience(campaignId)
      .then(response => {
        if (response.errors && response.errors.length > 0) {
          return
        }

        if (
          response.data?.campaignSuggestedAudience.suggestedAudience &&
          response.data?.campaignSuggestedAudience.suggestedImpressions
        ) {
          store.dispatch(
            campaignFormActions.setSuggestions({
              suggestedAudience: response.data.campaignSuggestedAudience.suggestedAudience,
              suggestedImpressions: response.data.campaignSuggestedAudience.suggestedImpressions,
            })
          )
        }
      })
      .finally(() => {
        store.dispatch(campaignFormActions.setSuggestionsLoading(false))
      })
  }
}
